/**
 * Responsibility: describe the mountains biome — how tall its terrain is at any
 * coordinate and what colours it uses.
 *
 * A biome descriptor is plain data plus factory functions. It contains no Three.js and
 * no DOM access, so it is fully unit testable, and the renderer consumes its output
 * without knowing which biome produced it.
 */

import { createRidgedFbm } from '../noise.js';
import { MOUNTAINS, PALETTE } from '../../config.js';

export const mountains = {
  id: 'mountains',
  label: 'Mountains',

  palette: PALETTE.mountains,

  /**
   * Added to this biome's suitability field before it is compared with the others. The dial
   * for how much of the world is mountainous.
   */
  suitabilityBias: MOUNTAINS.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * Ridged noise is used rather than plain fBm because plain fBm produces rounded blobs.
   * Folding the field at zero turns what were zero crossings into sharp crests, which is
   * what makes a mountain range read as a mountain range.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const ridged = createRidgedFbm(seed, 'elevation:mountains', {
      octaves: MOUNTAINS.OCTAVES,
      baseFrequency: 1 / MOUNTAINS.RIDGE_WAVELENGTH,
      persistence: MOUNTAINS.PERSISTENCE,
      lacunarity: MOUNTAINS.LACUNARITY,
    });

    return function sampleMountainElevation(x, z) {
      // Ridged noise is already in [0, 1]. Raising it to a power above 1 pushes mid
      // values down, deepening valleys and leaving peaks narrow.
      const shaped = Math.pow(ridged(x, z), MOUNTAINS.PEAK_SHARPNESS);
      return shaped * MOUNTAINS.MAX_ELEVATION;
    };
  },

  /**
   * Highest elevation this biome can produce, in world units.
   *
   * Used to normalise elevation into a 0..1 range for colour selection, and later to set
   * the flight ceiling so the aircraft always clears the terrain.
   */
  maxElevation: MOUNTAINS.MAX_ELEVATION,
};
